import { colorBaseGreyWarm600, colorBaseOrange500, IconHeart, Tooltip } from '@issuu/silkscreen';
import React, { useCallback, useContext, useState } from 'react';
import styled, { css } from 'styled-components';

import { toggleLikeDocument } from '../../api';
import { isLayoutOneOf } from '../../util/layoutUtils';
import DocumentContext from '../DocumentPage/DocumentContext';
import { checkLogin } from '../shared/check-login';
import { ActionButton, ActionIcon } from './ActionButton';
import IconFullHeart from './TempHeart';

// Using styled here gives an easy way to address these icons in
// HeartContainer, below.
const IconLikeHeart = styled(IconFullHeart)``;
const IconUnlikeHeart = styled(IconHeart)``;

// Like status for icons is controlled from this component, as
// styled-components leaks props through to svg elements.
const HeartContainer = styled(ActionIcon)<{ isLiked?: boolean }>`
    position: relative;

    ${IconLikeHeart},
    ${IconUnlikeHeart} {
        position: absolute;
        top: 0;
        left: 0;
        transition: 0.5s;
    }

    ${IconLikeHeart} {
        ${props =>
            props.isLiked
                ? css`
                      color: ${colorBaseOrange500};
                      z-index: 1;
                      visibility: visible;
                  `
                : css`
                      color: transparent;
                      z-index: 0;
                      visibility: hidden;
                  `}
    }
    ${IconUnlikeHeart} {
        ${props =>
            props.isLiked
                ? css`
                      color: transparent;
                      visibility: hidden;
                  `
                : css`
                      visibility: visible;
                  `}
    }
`;

const LikeCount = styled.div`
    background-color: ${colorBaseGreyWarm600};
    border-radius: 50%;
    margin-left: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5em;
    width: 1.5em;
`;

const Caption = styled.span<{ isTiny: boolean }>`
    ${props =>
        props.isTiny
            ? css`
                  text-align: center;
                  min-width: 4em;
              `
            : css`
                  text-align: left;
              `}
`;

const Like: React.FC = () => {
    const {
        document: { documentName, likeCount },
        publisher: { username },
        visitor,
        appState: {
            currentLayout: { name: layout },
        },
    } = useContext(DocumentContext);
    const [isDocumentLiked, setIsDocumentLiked] = useState<boolean>(
        visitor.isLoggedIn ? visitor.isDocumentLiked : false,
    );
    const [currentLikeCount, setCurrentLikeCount] = useState<number>(likeCount);
    const toggleLikeStatus = useCallback(() => {
        checkLogin(visitor.isLoggedIn, () => {
            setIsDocumentLiked(!isDocumentLiked);
            toggleLikeDocument(username, documentName, !isDocumentLiked)
                .then(({ likeCount, isDocumentLiked }) => {
                    setCurrentLikeCount(likeCount);
                    setIsDocumentLiked(isDocumentLiked);
                })
                .catch(() => {
                    setIsDocumentLiked(isDocumentLiked);
                });
        });
    }, [isDocumentLiked]);
    const isSmall = isLayoutOneOf(['tiny', 'small'], layout);
    return (
        <Tooltip id="like_tooltip" size="medium" text="Like to get better recommendations">
            <ActionButton aria-describedby="like_tooltip" onClick={toggleLikeStatus}>
                <HeartContainer isLiked={isDocumentLiked}>
                    <IconLikeHeart title="Liked" />
                    <IconUnlikeHeart title="Unliked" />
                </HeartContainer>
                <Caption isTiny={layout === 'tiny'}>{isDocumentLiked ? 'Unlike' : 'Like'}</Caption>
                {isSmall ? null : <LikeCount>{currentLikeCount}</LikeCount>}
            </ActionButton>
        </Tooltip>
    );
};

export default Like;
